import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Drawer,
  DropDown,
  Label,
  MLPVersion,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import CampaignService from '../../../sdk/services/campaignService';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { DynamicObjectInterface } from '../../../sdk/type';
import { useAPICall } from '../../../hooks';
import TeamService from '../../../sdk/services/teamService';
// import CoachService from '../../../sdk/services/coachService';
// import { CoachData } from '../coach-setup';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { getTeamListConfig, dataSearchOption, TeamData, CampaignOption } from './teamsListConfig';
import styles from './index.module.scss';

// type MasterData = {
//   coachList: CoachData[];
// };

export const AdminTeamsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  const [selectedRow, setSelectedRow] = useState<TeamData>();
  const [showDelete, setShowDelete] = useState(false);
  const [data, setData] = useState<TeamData[]>([]);
  //const [masterData, setMasterData] = useState<MasterData>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaignValue, setSelectedCampaignValue] = useState<CampaignOption>();
  //const [searchConfig, setSearchConfig] = useState(dataSearchOption);
  const [showDetails, setShowDetails] = useState(false);
  const [displayEmpty, setDisplayEmpty] = useState(false);

  useEffect(() => {
    //fetchMasterData();
    fetchCampaignList();
  }, []);

  useEffect(() => {
    // Once campaignList is fetched, set the default value for selectedCampaignValue
    if (campaignList.length > 0) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';

      if (campaignId) {
        setSelectedCampaignValue(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaignValue) {
      fetchTeamList();
    }
  }, [selectedCampaignValue]);

  // useEffect(() => {
  //   if (masterData) {
  //     setSearchConfig(_searchConfig => {
  //       let item = _searchConfig?.find(s => s.key === 'coachId');
  //       if (!item) return _searchConfig;

  //       item.dataset = masterData.coachList?.map((m: DynamicObjectInterface) => ({
  //         key: m.coachID,
  //         value: m.coachID,
  //         title: m.coachID + ' - ' + m.name,
  //       }));
  //       return _searchConfig.map(s => (s.key === item?.key ? item : s));
  //     });
  //   }
  // }, [masterData]);

  const fetchTeamList = async () => {
    let campaignId = selectedCampaignValue?.value || '';
    const result = await apiCall(TeamService.getTeamList, campaignId);

    if (result.status === 200) {
      let fetchedData = result.data.data ?? [];

      // Filter the fetched data based on the selected campaign value
      let filteredData = fetchedData;
      if (selectedCampaignValue?.value) {
        filteredData = fetchedData.filter(
          (team: DynamicObjectInterface) => team.campaignId === selectedCampaignValue.value,
        );
      }

      const _formattedData = filteredData.map((item: DynamicObjectInterface) => {
        item.isIndividualTeam = item.isIndividualTeam === 1 ? 'INDIVIDUAL' : 'TEAM';
        item.isGroupAchieved =
          item.isGroupAchieved === 1 ? 'YES' : item.isGroupAchieved === 0 ? 'NO' : 'PENDING';
        return { ...item };
      });

      setData(_formattedData);
      setDisplayEmpty(filteredData.length === 0);
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve team list');
    }
  };

  const fetchCampaignList = async () => {
    const result = await apiCall(CampaignService.getAllCampaigns);
    if (result.status === 200 && result?.data?.data?.length > 0) {
      const campaigns = result.data.data
       .filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
        )
        .map((campaign: DynamicObjectInterface) => ({
          ...campaign,
          title: `${campaign.campaignId} - ${campaign.campaignName}`,
          value: campaign.campaignId,
        }))
        .sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => {
          if (a.campaignStart > b.campaignStart) return -1;
          if (a.campaignStart < b.campaignStart) return 1;
          return 0;
        });
      setCampaignList(campaigns);
    } else {
      LoggerService.log('Failed to retrieve coach list by campaign');
    }
  };

  // const fetchMasterData = async () => {
  //   let list = {
  //     coachList: {
  //       endpoint: CoachService.getCoachList,
  //     },
  //   };
  //   try {
  //     const results = await Promise.all(Object.values(list)?.map(e => apiCall(e.endpoint)));

  //     setMasterData(
  //       Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
  //         result[key] = results[idx]?.data?.data;
  //         return result;
  //       }, {}),
  //     );
  //   } catch (error) {
  //     LoggerService.log('Failed to retrieve coach list', error);
  //   }
  // };

  const handleInputChange = (value: DynamicObjectInterface) => {
    navigate(`?campaignId=${value.campaignId}`);
  };

  const onEdit = (id: string) => {
    navigate(`/admin/manage-team/edit?=${id}`);
  };

  const onDelete = (obj: TeamData) => {
    setSelectedRow(obj);
    setShowDelete(true);
    setShowDetails(false);
  };

  const onDeleteAction = async () => {
    const result = await apiCall(TeamService.deleteTeam, `${selectedRow?.teamId}`);
    if (result.status === 200) {
      LoggerService.log('Team deleted successfully');
      adminToast({
        message: 'Team deleted successfully',
        theme: Theme.SUCCESS,
      });
      fetchTeamList();
    } else {
      LoggerService.log('Error deleting Team');
      adminToast({
        message: 'Error deleting Team',
        theme: Theme.ERROR,
      });
    }
    setShowDelete(false);
  };

  const onRowClick = (obj: TeamData) => {
    setSelectedRow(obj);
    setShowDetails(true);
  };

  const buttonConfig = [
    {
      title: t('admin.teams.addNewTeam'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/manage-team/create-team`);
      },
    },
  ];

  const drawerDetails = (
    <>
      {showDetails && (
        <Drawer
          onClose={() => setShowDetails(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          header={<div>Details</div>}
          bottomGapFooter={'0px'}
          footer={
            <div className={styles['add-btn-wrapper']}>
              <Button
                title="Delete"
                onClick={() => onDelete(selectedRow!)}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
              <Button
                title="Edit"
                onClick={() => onEdit(selectedRow?.teamId?.toString() ?? '')}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.teams.campaignId')} value={selectedRow?.campaignId ?? '-'} />
                  <Label title={t('admin.teams.campaignName')} value={selectedRow?.campaignName ?? '-'} />
                </div>
                <Label
                  title={t('admin.teams.campaignPeriod')}
                  value={`${selectedRow?.campaignStart ? dateTimeToCustomFormat(selectedRow?.campaignStart) : ''} - ${
                    selectedRow?.campaignEnd ? dateTimeToCustomFormat(selectedRow?.campaignEnd) : ''
                  }`}
                />
                <hr className={styles['horizontal-divider']} />
                <div className={styles['two-columns']}>
                  <Label title={t('admin.teams.teamName')} value={selectedRow?.teamName ?? '-'} />
                  <Label title={t('admin.teams.description')} value={selectedRow?.description ?? '-'} />
                </div>
                <div className={styles['two-columns']}>
                  <Label
                    title={t('admin.teams.teamStartDate')}
                    value={selectedRow?.teamStartDate ? dateTimeToCustomFormat(selectedRow?.teamStartDate) : '-'}
                  />
                  <Label
                    title={t('admin.teams.teamEndDate')}
                    value={selectedRow?.teamEndDate ? dateTimeToCustomFormat(selectedRow?.teamEndDate) : '-'}
                  />
                </div>
                <Label title={t('admin.teams.status')} value={selectedRow?.status ?? '-'} />
                <hr className={styles['horizontal-divider']} />
                <div className={styles['two-columns']}>
                  <Label title={t('admin.teams.coachId')} value={selectedRow?.coachId ?? '-'} />
                  {/* <Label
                    title={t('admin.teams.coachName')}
                    value={masterData?.coachList?.find(c => c.coachID === selectedRow?.coachId)?.name ?? '-'}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.teams.deleteTeam')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.teams.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.teams.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <>
            <span>Team ID:{selectedRow?.teamId ?? '-'}</span>
            <br />
            <span>Team Name: {selectedRow?.teamName ?? '-'}</span>
          </>
        </Modal>
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.teams.title')} button={buttonConfig} />
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaignValue?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedItem: DynamicObjectInterface) => {
              handleInputChange(selectedItem);
            },
          }}
          readOnly={false}
        />
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          handleReset={undefined}
          handleSearch={undefined}
          searchByStrip
          getColumns={getTeamListConfig}
          data={dataSearchOption}
          rowKey="teamId"
          selected={selectedRow?.teamId?.toString()}
          cb={{ onEdit, onDelete }}
          onRowClick={row => onRowClick(row)}
        />
      )}
      {deletePrompt}
      {drawerDetails}
    </div>
  );
};
